import { Link } from "react-router-dom";
import { Linkedin, Twitter, Youtube, Facebook, Mail, MapPin, Clock } from "lucide-react";

import { Container } from "@/components/ui/Container";
import { Logo } from "@/components/common/Logo";
import { NAV_ITEMS, SITE_CONTACT, SITE_NAME } from "@/data/site";

const socialIcons: Record<string, typeof Mail> = {
  linkedin: Linkedin,
  twitter: Twitter,
  youtube: Youtube,
  facebook: Facebook,
  mail: Mail,
};

// What-we-do links.
const WHAT_WE_DO = [
  { label: "Services", path: "/services" },
  { label: "For Sites & Physicians", path: "/services#for-sites" },
  { label: "For Pharma, Biotech & CROs", path: "/services#for-pharma" },
  { label: "Therapeutic Areas", path: "/therapeutic-areas" },
  { label: "Our Sites", path: "/our-sites" },
  { label: "Blogs", path: "/blogs" },
];

export function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer className="border-t border-border bg-background" data-testid="footer">
      {/* Emerald accent line */}
      <div className="h-0.5 w-full bg-primary" aria-hidden="true" />

      <Container className="grid grid-cols-1 gap-10 py-14 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
        {/* Company */}
        <div>
          <Logo variant="footer" />
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-muted-foreground">
            {SITE_CONTACT.tagline}
          </p>
          <div className="mt-5 flex items-center gap-2">
            {SITE_CONTACT.social.map((s) => {
              const Icon = socialIcons[s.icon] ?? Mail;
              return (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  data-testid={`footer-social-${s.icon}`}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
                >
                  <Icon size={18} />
                </a>
              );
            })}
          </div>
        </div>

        {/* Quick links */}
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground">
            Quick Links
          </h3>
          <ul className="mt-4 space-y-3">
            {NAV_ITEMS.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className="text-sm text-muted-foreground transition-colors hover:text-primary"
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* What we do */}
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground">
            What We Do
          </h3>
          <ul className="mt-4 space-y-3">
            {WHAT_WE_DO.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className="text-sm text-muted-foreground transition-colors hover:text-primary"
                >
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground">
            Get in Touch
          </h3>
          <ul className="mt-4 space-y-3 text-sm text-muted-foreground">
            <li className="flex items-start gap-2.5">
              <MapPin size={16} className="mt-0.5 shrink-0 text-accent" />
              <span>{SITE_CONTACT.addressLines.join(", ")}</span>
            </li>
            <li className="flex items-start gap-2.5">
              <Mail size={16} className="mt-0.5 shrink-0 text-accent" />
              <a href={`mailto:${SITE_CONTACT.email}`} className="hover:text-primary">
                {SITE_CONTACT.email}
              </a>
            </li>
            <li className="flex items-start gap-2.5">
              <Clock size={16} className="mt-0.5 shrink-0 text-accent" />
              <span>
                {SITE_CONTACT.hours.map((h) => `${h.days}: ${h.time}`).join(" ")}
              </span>
            </li>
          </ul>
        </div>
      </Container>

      {/* Bottom bar */}
      <div className="border-t border-border">
        <Container className="flex flex-col items-center justify-between gap-4 py-6 sm:flex-row">
          <p className="text-sm text-muted-foreground" data-testid="footer-legal">
            &copy; {year} {SITE_NAME}. All rights reserved.
          </p>
          <div className="flex items-center gap-6 text-sm">
            <Link to="/privacy" className="text-muted-foreground transition-colors hover:text-primary">
              Privacy Policy
            </Link>
            <Link to="/" className="text-muted-foreground transition-colors hover:text-primary">
              Terms of Service
            </Link>
          </div>
        </Container>
      </div>
    </footer>
  );
}
